import { useEffect, useState, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

import { useErrorHandler } from './useErrorHandler';
import { useAlertContext } from '@/contexts/alert.context';

export interface CRUDListService<T> {
  getAll: () => Promise<T[]>;
  delete: (id: string) => Promise<void>;
}

export interface UseCRUDListOptions<T> {
  service: CRUDListService<T>;
  entityName: string;
  routes?: {
    new: string;
    edit: (id: string) => string;
  };
  messages?: {
    loadError?: string;
    deleteSuccess?: string;
    deleteError?: string;
  };
  fetchOnMount?: boolean;
}

export interface UseCRUDListReturn<T> {
  items: T[];
  loading: boolean;
  fetchItems: () => Promise<void>;
  handleNew: () => void;
  handleEdit: (id: string) => void;
  handleDelete: (id: string) => Promise<void>;
}

export function useCRUDList<T extends { id: string }>({
  service,
  entityName,
  routes,
  messages,
  fetchOnMount = true,
}: UseCRUDListOptions<T>): UseCRUDListReturn<T> {
  const [items, setItems] = useState<T[]>([]);
  const [loading, setLoading] = useState(false);
  const hasFetched = useRef(false);

  const navigate = useNavigate();
  const { handleError } = useErrorHandler();
  const { notify } = useAlertContext();

  const defaultMessages = {
    loadError: `Erro ao carregar ${entityName}`,
    deleteSuccess: `${entityName} excluído(a) com sucesso!`,
    deleteError: `Erro ao excluir ${entityName}`,
    ...messages,
  };

  const fetchItems = useCallback(async () => {
    setLoading(true);
    try {
      const response = await service.getAll();
      setItems(response);
    } catch (error) {
      handleError(error, defaultMessages.loadError);
    } finally {
      setLoading(false);
    }
  }, [service, handleError, defaultMessages.loadError]);

  const handleNew = useCallback(() => {
    if (routes?.new) {
      navigate(routes.new);
    }
  }, [navigate, routes?.new]);

  const handleEdit = useCallback((id: string) => {
    if (routes?.edit) {
      navigate(routes.edit(id));
    }
  }, [navigate, routes?.edit]);

  const handleDelete = useCallback(async (id: string) => {
    try {
      await service.delete(id);
      notify({
        type: 'success',
        message: defaultMessages.deleteSuccess,
      });
      fetchItems();
    } catch (error) {
      handleError(error, defaultMessages.deleteError);
    }
  }, [service, notify, handleError, fetchItems, defaultMessages.deleteSuccess, defaultMessages.deleteError]);

  useEffect(() => {
    if (fetchOnMount && !hasFetched.current) {
      hasFetched.current = true;
      fetchItems();
    }
  }, [fetchOnMount, fetchItems]);

  return {
    items,
    loading,
    fetchItems,
    handleNew,
    handleEdit,
    handleDelete,
  };
}
